import { Close } from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import useStorageState from '~/hooks/useStorageState';
import { getTrippedStatus } from '~/views/Dashboard/AdManager/OverallCaps';
import { useOverallCaps } from '~/views/Dashboard/AdManager/OverallCaps/utils';
import useClientPlQuery from '../../../hooks/queries/useClientPlQuery';
import useSessionPlQuery from '../../../hooks/queries/useSessionPlQuery';
import Link from '../Link/Link';

function getSuspensionMessage(suspensionReason: string, accountId?: number | string) {
  switch (suspensionReason) {
    case 'Dispute':
      return (
        <div>
          A Payment has been Disputed for this account. All campaign activity has been suspended.
          <br />
          Please contact{' '}
          <a href="mailto:help@dianomi.com" target="_blank" rel="noreferrer">
            help@dianomi.com
          </a>{' '}
          to resolve the dispute.
        </div>
      );
    case 'Arrears':
      return (
        <div>
          This account is in arrears - All campaigns activity has been suspended.
          <br />
          Please update your payment method or contact{' '}
          <a href="mailto:help@dianomi.com" target="_blank" rel="noreferrer">
            help@dianomi.com
          </a>
          .
        </div>
      );
    case 'Incomplete Billing':
      return (
        <div>
          This account has an incomplete billing profile.
          <br />
          <span>
            Please update your billing information{' '}
            <Link to={`/client/${accountId}/billing`}>Here</Link> or contact{' '}
            <a href="mailto:help@dianomi.com" target="_blank" rel="noreferrer">
              help@dianomi.com
            </a>
          </span>
        </div>
      );
    default:
      return (
        <>
          Account Suspended. All campaigns activity has been stopped <br />
          Please contact{' '}
          <a href="mailto:help@dianomi.com" target="_blank" rel="noreferrer">
            help@dianomi.com
          </a>
          for more information.
        </>
      );
  }
}

function AccountAlertBanner({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose?: () => void;
}) {
  return (
    <Box
      role="alert"
      component="h4"
      sx={{
        backgroundColor: 'error.main',
        color: 'white',
        textAlign: 'center',
        margin: 0,
        padding: 0,
        fontSize: { xs: '12px', md: '14px', lg: '16px' },
        '& a': {
          color: 'secondary.main',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        {children}
        {onClose && (
          <Tooltip
            title="Dismiss this alert"
            slotProps={{
              popper: {
                sx: { zIndex: 4000 },
              },
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                margin: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: 0.5,
                color: 'white',
              }}
            >
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

export function AccountSuspendedIndicator() {
  const clientPlQuery = useClientPlQuery();
  const sessionQuery = useSessionPlQuery((data) => ({
    id: data.id,
    currency: data.currency_pdf_name,
  }));
  const overallCapsQuery = useOverallCaps(sessionQuery.data?.id, sessionQuery.data?.currency);
  const [ignoredAlerts, setIgnoredAlerts] = useStorageState(
    `ignored-alerts`,
    {} as Record<string, { [key: string]: boolean }>,
    'session',
  );

  if (!clientPlQuery.data || !sessionQuery.data) {
    return null;
  }

  if (clientPlQuery.data.suspensionReason) {
    const suspensionMessage = getSuspensionMessage(
      clientPlQuery.data.suspensionReason,
      sessionQuery.data?.id,
    );
    return <AccountAlertBanner>{suspensionMessage}</AccountAlertBanner>;
  }
  const accountIgnoredAlerts = ignoredAlerts[sessionQuery.data?.id.toString()];
  const clientId = sessionQuery.data?.id.toString();
  const tripped = !!getTrippedStatus(
    overallCapsQuery.data?.filter((cap) => cap.period !== 'Daily'), // we don't want to trigger an alert for daily caps
  );

  if (tripped && !accountIgnoredAlerts?.cap) {
    return (
      <AccountAlertBanner
        onClose={() => setIgnoredAlerts({ ...ignoredAlerts, [clientId]: { cap: true } })}
      >
        <Typography sx={{ fontSize: { xs: '12px', md: '14px', lg: '16px' } }}>
          An account-wide cap has been triggered. All campaign activity has been stopped -{' '}
          <Link to={`/client/${clientId}/account/parameters`}>Click here to manage your caps</Link>
        </Typography>
      </AccountAlertBanner>
    );
  }
  return null;
}
